import TurndownService from 'turndown';
import {gfm} from 'turndown-plugin-gfm';

import {sanitizeHtml} from '../sanitization/domPurify';

/*
 * Turndown configuration.
 */

const turndownOptions: TurndownService.Options = {};

const turndownPlugin: TurndownService.Plugin = (turndown: TurndownService) => {
  // Add GitHub flavor.
  turndown.use(gfm);

  // Treat divs as lines, not as CommonMark paragraphs.
  turndown.addRule('line', {
    filter: 'div',
    replacement: (content) => `\n${content}\n`,
  });

  // Treat breaks as empty lines, not as CommonMark paragraphs.
  turndown.addRule('lineBreak', {
    filter: 'br',
    replacement: () => '\n',
  });

  // Remove the content of colgroup elements.
  turndown.remove('colgroup');
};

/*
 * API.
 */

/** Converts HTML from the composer to Markdown. */
export function htmlToMarkdown(html: string) {
  // Clean the HTML.
  const cleanHtml = sanitizeHtml(html, {
    hooks: {uponSanitizeElement},
  });

  // Convert it to Markdown.
  const turndown = new TurndownService(turndownOptions);
  turndown.use(turndownPlugin);
  return turndown.turndown(cleanHtml);
}

/*
 * Sanitization hooks.
 */

/** Custom logic to help converting HTML to Markdown. */
function uponSanitizeElement(currentNode: Element) {
  emojiImageToColons(currentNode);
}

/** Replace emoji images with their colons text representation. */
function emojiImageToColons(currentNode: Element) {
  const document = currentNode.ownerDocument;

  // Only applies to images with parent nodes (the parent node is necessary to `replaceChild`).
  if (!document || currentNode.tagName !== 'IMG' || !currentNode.parentNode) {
    return;
  }

  // Extract the colons representation.
  const emojiColons =
    currentNode.getAttribute('data-front-emoji-shortcode') ??
    currentNode.getAttribute('front-emoji-shortcode');
  if (!emojiColons) {
    return;
  }

  // Replace this node by the colons representation.
  const emojiContainer = document.createElement('span');
  emojiContainer.textContent = emojiColons;
  currentNode.parentNode.replaceChild(emojiContainer, currentNode);
}
